import React, { useState, useEffect, useCallback, useRef } from "react";
import PageHeader from "./PageHeader";
import DictateButton from "./DictateButton";
import { Sparkles, Pencil, Mail, Send, Loader, Check, Copy, Plus, User, Trash2 } from "lucide-react";

// Diktiertes Satzstück an bestehenden Text anhängen (mit sauberem Leerzeichen).
const appendSpeech = (prev, t) => (prev && prev.trim() ? prev.replace(/\s*$/, "") + " " : "") + t;

const MODES = [
  { id: "generieren", title: "Antwort generieren", desc: "Aus Stichpunkten eine fertige Mail schreiben", icon: Sparkles },
  { id: "ueberarbeiten", title: "Antwort überarbeiten", desc: "Deinen fertigen Entwurf nach den UNEQ-Kriterien schärfen", icon: Pencil },
];

function stageText(stage) {
  if (!stage || stage === "start") return "Arbeite …";
  if (stage === "analyse") return "Ziel & Nutzen analysieren …";
  if (stage === "writing") return "Schreibe Entwurf …";
  if (stage === "reviewing") return "Prüfe gegen die Kriterien …";
  if (stage === "rewriting") return "Überarbeite …";
  return "Arbeite …";
}

const mailText = (sections) => (sections || []).map((s) => s.content).filter(Boolean).join("\n\n");

function firstMeaningfulLine(...texts) {
  for (const t of texts) {
    const line = (t || "").split("\n").map((s) => s.replace(/^[-•\s]+/, "").trim()).find(Boolean);
    if (line) return line;
  }
  return "Mail-Antwort";
}

function MailBubble({ sections, onCopy, copied }) {
  return (
    <div className="email-mail">
      <pre className="email-mail-text">{mailText(sections)}</pre>
      <button className="email-copy-btn" onClick={onCopy}>
        {copied ? <><Check size={13} /> Kopiert</> : <><Copy size={13} /> Mail kopieren</>}
      </button>
    </div>
  );
}

export default function EmailPage() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState("new"); // "new" | number | null
  const [messages, setMessages] = useState([]);
  const [origForSession, setOrigForSession] = useState("");

  // Setup-Formular (Modus "new")
  const [mode, setMode] = useState("generieren");
  const [originalEmail, setOriginalEmail] = useState("");
  const [bullets, setBullets] = useState("");
  const [draft, setDraft] = useState("");

  const [refineText, setRefineText] = useState("");
  const [stage, setStage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const threadRef = useRef(null);

  const loadSessions = useCallback(() => {
    fetch("/api/mail/sessions", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setSessions(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages, stage]);

  const stream = async (body) => {
    setBusy(true); setStage("start");
    try {
      const res = await fetch("/api/generate/email", {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(body),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "", sections = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n"); buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const d = JSON.parse(line.slice(6));
            if (d.type === "progress") setStage(d.stage);
            else if (d.type === "result") sections = d.sections;
            else if (d.type === "error") throw new Error(d.error);
          } catch (e) { if (e.message && e.message.startsWith("Fehler")) throw e; }
        }
      }
      return sections;
    } finally { setStage(null); setBusy(false); }
  };

  const saveMessage = (sid, msg) =>
    fetch(`/api/mail/sessions/${sid}/messages`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify(msg),
    }).catch(() => {});

  const canStart = mode === "ueberarbeiten" ? draft.trim() : (originalEmail.trim() || bullets.trim());

  const start = async () => {
    if (!canStart || busy) return;
    const summary = mode === "ueberarbeiten"
      ? `Entwurf überarbeiten${originalEmail.trim() ? " (mit Bezug auf die erhaltene Mail)" : ""}`
      : `Antwort generieren${bullets.trim() ? ` aus ${bullets.trim().split("\n").filter(Boolean).length} Stichpunkt(en)` : ""}`;
    const title = firstMeaningfulLine(mode === "ueberarbeiten" ? draft : bullets, originalEmail).slice(0, 60);

    // Session anlegen
    let sid;
    try {
      const res = await fetch("/api/mail/sessions", {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ mode, originalEmail, bullets, draft, title }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Fehler");
      sid = d.session.id;
    } catch (err) { alert("Konnte Verlauf nicht anlegen: " + err.message); return; }

    setActiveId(sid);
    setOrigForSession(originalEmail);
    const setupMsg = { role: "user", kind: "setup", content: summary };
    setMessages([setupMsg]);
    saveMessage(sid, setupMsg);
    loadSessions();

    try {
      const sections = await stream({ mode, originalEmail, bulletPoints: bullets, draft });
      const am = sections ? { role: "assistant", kind: "mail", sections } : { role: "assistant", kind: "error", content: "Keine Mail erhalten." };
      setMessages((m) => [...m, am]);
      saveMessage(sid, am);
    } catch (err) {
      const em = { role: "assistant", kind: "error", content: err.message };
      setMessages((m) => [...m, em]);
      saveMessage(sid, em);
    }
    loadSessions();
  };

  const refine = async () => {
    const instr = refineText.trim();
    if (!instr || busy || typeof activeId !== "number") return;
    const lastMail = [...messages].reverse().find((m) => m.kind === "mail");
    if (!lastMail) return;
    setRefineText("");
    const um = { role: "user", kind: "text", content: instr };
    setMessages((m) => [...m, um]);
    saveMessage(activeId, um);
    try {
      const sections = await stream({ mode: "ueberarbeiten", originalEmail: origForSession, draft: mailText(lastMail.sections), instruction: instr });
      if (sections) { const am = { role: "assistant", kind: "mail", sections }; setMessages((m) => [...m, am]); saveMessage(activeId, am); }
    } catch (err) {
      const em = { role: "assistant", kind: "error", content: err.message };
      setMessages((m) => [...m, em]); saveMessage(activeId, em);
    }
    loadSessions();
  };

  const openSession = async (id) => {
    setActiveId(id); setMessages([]); setRefineText("");
    try {
      const res = await fetch(`/api/mail/sessions/${id}`, { credentials: "include" });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setMessages((d.messages || []).map((m) => ({ role: m.role, kind: m.kind, content: m.content, sections: m.sections })));
      setOrigForSession(d.session.original_email || "");
    } catch (err) { /* ignore */ }
  };

  const startNew = () => {
    setActiveId("new"); setMessages([]); setRefineText("");
    setMode("generieren"); setOriginalEmail(""); setBullets(""); setDraft("");
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Diesen Mail-Verlauf löschen?")) return;
    await fetch(`/api/mail/sessions/${id}`, { method: "DELETE", credentials: "include" });
    if (activeId === id) startNew();
    loadSessions();
  };

  const copy = (sections, idx) => {
    navigator.clipboard.writeText(mailText(sections));
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx((c) => (c === idx ? null : c)), 2000);
  };

  return (
    <>
      <PageHeader
        eyebrow="Texte"
        title="E-Mails"
        subtitle="Partner-Mails im UNEQ-Stil – Antwort generieren oder Entwurf überarbeiten. Deine Verläufe werden gespeichert."
      />

      <div className="chat-layout">
        <div className="chat-sidebar card">
          <button className="btn-primary chat-new-btn" onClick={startNew}><Plus size={16} /> Neue Mail</button>
          <div className="chat-session-list">
            {sessions.length === 0 && <div className="chat-session-empty">Noch keine Mail-Verläufe.</div>}
            {sessions.map((s) => (
              <div key={s.id} className={"chat-session-row" + (activeId === s.id ? " is-active" : "")} onClick={() => openSession(s.id)}>
                <Mail size={14} />
                <div className="chat-session-info">
                  <span className="chat-session-title">{s.title || "Mail-Antwort"}</span>
                  <span className="chat-session-sub">{s.mode === "ueberarbeiten" ? "Überarbeiten" : "Generieren"}</span>
                </div>
                <button className="chat-session-del" onClick={(e) => deleteSession(s.id, e)} title="Löschen"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="chat-main card">
          {activeId === null && (
            <div className="chat-placeholder">
              <div className="chat-placeholder-icon"><Mail size={32} strokeWidth={1.75} /></div>
              <p>Wähle links einen Verlauf oder starte eine neue Mail.</p>
            </div>
          )}

          {activeId === "new" && (
            <div className="chat-wizard email-setup">
              <div className="chat-wizard-header">
                <div className="chat-wizard-badge"><Mail size={20} /></div>
                <h3 className="chat-wizard-title">Neue Mail</h3>
                <p className="chat-wizard-lead">Antwort an einen Partner – im UNEQ-Stil, mit Nutzen vorne und klarem nächsten Schritt.</p>
              </div>

              <div className="chat-wizard-field">
                <label>Was möchtest du tun?</label>
                <div className="chat-opt-grid">
                  {MODES.map((mo) => {
                    const Icon = mo.icon;
                    return (
                      <button key={mo.id} type="button" className={"chat-opt" + (mode === mo.id ? " is-active" : "")} onClick={() => setMode(mo.id)}>
                        <div className="chat-opt-icon"><Icon size={18} /></div>
                        <div className="chat-opt-body">
                          <span className="chat-opt-title">{mo.title}</span>
                          <span className="chat-opt-desc">{mo.desc}</span>
                        </div>
                        <span className="chat-opt-check"><Check size={13} strokeWidth={3} /></span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="chat-wizard-field">
                <label>Erhaltene Mail <span className="email-optional">(worauf du antwortest – optional)</span></label>
                <textarea className="ai-prompt-input email-textarea" rows={5}
                  placeholder="Füge hier die Mail ein, auf die du antwortest …"
                  value={originalEmail} onChange={(e) => setOriginalEmail(e.target.value)} />
              </div>

              {mode === "generieren" ? (
                <div className="chat-wizard-field">
                  <div className="email-label-row">
                    <label>Deine Antwort in Stichpunkten</label>
                    <DictateButton label="Antwort diktieren" onResult={(t) => setBullets((p) => appendSpeech(p, t))} />
                  </div>
                  <textarea className="ai-prompt-input email-textarea" rows={5}
                    placeholder="Was soll in die Antwort? Ein Stichpunkt pro Zeile … oder per Mikrofon diktieren."
                    value={bullets} onChange={(e) => setBullets(e.target.value)} />
                </div>
              ) : (
                <div className="chat-wizard-field">
                  <div className="email-label-row">
                    <label>Dein Antwort-Entwurf</label>
                    <DictateButton label="Entwurf diktieren" onResult={(t) => setDraft((p) => appendSpeech(p, t))} />
                  </div>
                  <textarea className="ai-prompt-input email-textarea" rows={7}
                    placeholder="Füge hier deinen fertigen Entwurf ein … oder diktiere ihn per Mikrofon."
                    value={draft} onChange={(e) => setDraft(e.target.value)} />
                </div>
              )}

              <button className="btn-primary email-start-btn" disabled={!canStart || busy} onClick={start}>
                {busy ? <><Loader size={16} className="spin" /> Erstelle …</> : <><Sparkles size={16} /> {mode === "ueberarbeiten" ? "Mail überarbeiten" : "Mail erstellen"}</>}
              </button>
            </div>
          )}

          {typeof activeId === "number" && (
            <>
              <div className="chat-thread" ref={threadRef}>
                {messages.map((m, i) => m.role === "user" ? (
                  <div key={i} className="chat-msg chat-msg-user">
                    <div className="chat-avatar chat-avatar-user"><User size={15} /></div>
                    <div className="chat-bubble chat-bubble-user"><div className="chat-msg-text">{m.content}</div></div>
                  </div>
                ) : (
                  <div key={i} className="chat-msg chat-msg-assistant">
                    <div className="chat-avatar chat-avatar-assistant"><Mail size={15} /></div>
                    <div className="chat-bubble chat-bubble-assistant">
                      {m.kind === "mail"
                        ? <MailBubble sections={m.sections} copied={copiedIdx === i} onCopy={() => copy(m.sections, i)} />
                        : <div className="chat-msg-text">{m.content}</div>}
                    </div>
                  </div>
                ))}
                {stage && (
                  <div className="chat-msg chat-msg-assistant">
                    <div className="chat-avatar chat-avatar-assistant"><Mail size={15} /></div>
                    <div className="chat-bubble chat-bubble-assistant chat-stage">
                      <Loader size={14} className="icon-spin" /> {stageText(stage)}
                    </div>
                  </div>
                )}
              </div>

              <div className="chat-composer">
                <div className="chat-composer-field">
                  <textarea className="chat-input-box" rows={1}
                    placeholder="Was soll an der Mail noch anders sein? (z.B. kürzer, Nutzen stärker betonen …) – oder per Mikrofon diktieren."
                    value={refineText} onChange={(e) => setRefineText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); refine(); } }} />
                  <DictateButton label="" title="Änderung diktieren" onResult={(t) => setRefineText((p) => appendSpeech(p, t))} />
                  <button className="chat-send" onClick={refine} disabled={busy || !refineText.trim()} title="Überarbeiten">
                    {busy ? <Loader size={17} className="spin" /> : <Send size={17} />}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
